import { atom, AtomEffect, RecoilState } from "recoil";
import { deleteDB, IDBPDatabase, openDB } from "idb";

export function atomWithPersistence<T>(key: string, val: T): RecoilState<T> {
    return atom({
        key: key,
        default: val,
        effects: [localStorageEffect],
    });
}

/** Like atomWithPersistence, but support tabs share the viewer's own saved value instead of starting fresh. */
export function atomWithViewerPersistence<T>(key: string, val: T): RecoilState<T> {
    return atom({
        key: key,
        default: val,
        effects: [viewerStorageEffect],
    });
}

export function atomWithIndexedPersistence<T>(key: string, val: T): RecoilState<T> {
    return atom({
        key: key,
        default: val,
        effects: [idbStorageEffect],
    });
}

const DB = "ht-db";
const STORE = "ht-store";
const SUPPORT_TAB_KEY = "__ht_support_tab";
const SUPPORT_SESSION_ID_KEY = "__ht_support_session_id";

const dbPromises = new Map<string, Promise<IDBPDatabase>>();

const getSupportSessionIdFromPath = () => {
    if (typeof window === "undefined") {
        return "";
    }
    const match = window.location.pathname.match(/^\/support\/([^/]+)/);
    return match?.[1] || "";
}

const isSupportContext = () => {
    if (typeof window === "undefined") {
        return false;
    }
    const sessionIdFromPath = getSupportSessionIdFromPath();
    if (sessionIdFromPath) {
        window.sessionStorage.setItem(SUPPORT_TAB_KEY, "1");
        window.sessionStorage.setItem(SUPPORT_SESSION_ID_KEY, sessionIdFromPath);
        return true;
    }
    return window.sessionStorage.getItem(SUPPORT_TAB_KEY) === "1";
}

const getStorage = () => {
    return isSupportContext() ? window.sessionStorage : window.localStorage;
}

const getDbName = () => {
    if (!isSupportContext()) {
        return DB;
    }
    const supportSessionId = window.sessionStorage.getItem(SUPPORT_SESSION_ID_KEY) || "support";
    return `ht-db-support-${supportSessionId}`;
}

const getDbPromise = () => {
    const dbName = getDbName();
    const existing = dbPromises.get(dbName);
    if (existing) {
        return existing;
    }
    const dbPromise = openDB(dbName, 1, {
        upgrade(db) {
            db.createObjectStore(STORE);
        },
    });
    dbPromises.set(dbName, dbPromise);
    return dbPromise;
}

const getItemPromise = (key: string) => {
    return getDbPromise().then(db => db.get(STORE, key))
}

const PRESERVED_EXACT_KEYS = [
    "map.type",
    "account.username",
    "app.snoozenotifications",
    "map.provider",
    "app.desktop.download.dismissed",
    "map.dock.collapsed",
    "app.seenhints",
    "jobsheet.size",
    "jobsheet.collapsed",
];

const PRESERVED_TABLE_PREF_PREFIXES = [
    "view.zones.grid.columns",
    "view.places.grid.columns",
    "view.location.grid.columns",
    "view.members.grid.columns",
    "alljobs.columns",
    "alljobs.grid.columns.width",
    "alljobs.grid.columns.pinned",
    "view.jobs.grid.compact",
];

const PRESERVED_TABLE_PREFS_KEY = "_preserved_table_prefs";

export async function deleteDb() {
    if (isSupportContext()) {
        const dbName = getDbName();
        window.sessionStorage.clear();
        await deleteDB(dbName);
        dbPromises.delete(dbName);
        return;
    }

    const preservedItems = new Map<string, string | null>();
    PRESERVED_EXACT_KEYS.forEach((key) => preservedItems.set(key, localStorage.getItem(key)));

    const username = JSON.parse(localStorage.getItem("account.username") || '""');
    const tablePrefs: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && PRESERVED_TABLE_PREF_PREFIXES.some((prefix) => key.startsWith(prefix))) {
            const value = localStorage.getItem(key);
            if (value !== null) {
                tablePrefs[key] = value;
            }
        }
    }

    localStorage.clear();
    preservedItems.forEach((value, key) => {
        if (value !== null) {
            localStorage.setItem(key, value);
        }
    });

    if (username && Object.keys(tablePrefs).length > 0) {
        localStorage.setItem(PRESERVED_TABLE_PREFS_KEY, JSON.stringify({ username, prefs: tablePrefs }));
    }

    await deleteDB(DB);
    dbPromises.delete(DB);
}

export function restorePreservedTablePrefs(username: string) {
    if (isSupportContext()) return;
    const saved = localStorage.getItem(PRESERVED_TABLE_PREFS_KEY);
    localStorage.removeItem(PRESERVED_TABLE_PREFS_KEY);
    if (!saved) return;
    try {
        const { username: savedUsername, prefs } = JSON.parse(saved);
        if (savedUsername === username) {
            for (const [key, value] of Object.entries(prefs)) {
                localStorage.setItem(key, value as string);
            }
        }
    } catch {
        // ignore corrupted data
    }
}

/** Reads a value saved by a persisted atom, or undefined. */
export function readStoredValue<T>(key: string): T | undefined {
    try {
        const value = getStorage().getItem(key);
        return value === null || value === 'undefined' ? undefined : JSON.parse(value);
    } catch {
        return undefined;
    }
}

/** Like localStorageEffect, but stored under an explicit key (for atomFamily members). */
export function storageEffectForKey<T>(key: string): AtomEffect<T> {
    return ({ setSelf, onSet }) => {
        const savedValue = getStorage().getItem(key);
        if (savedValue !== null && savedValue !== 'undefined') {
            setSelf(JSON.parse(savedValue));
        }
        onSet((newValue, _, isReset) => {
            const targetStorage = getStorage();
            isReset
                ? targetStorage.removeItem(key)
                : targetStorage.setItem(key, JSON.stringify(newValue));
        });
    };
}

export const localStorageEffect: AtomEffect<any> = ({ setSelf, onSet, node }) => {
    const storage = getStorage();
    const savedValue = storage.getItem(node.key)
    if (savedValue !== null && savedValue !== 'undefined') {
        setSelf(JSON.parse(savedValue));
    }
    onSet((newValue, _, isReset) => {
        const targetStorage = getStorage();
        isReset
            ? targetStorage.removeItem(node.key)
            : targetStorage.setItem(node.key, JSON.stringify(newValue));
    });
};

const viewerStorageEffect: AtomEffect<any> = ({ setSelf, onSet, node }) => {
    const savedValue = window.localStorage.getItem(node.key);
    if (savedValue !== null && savedValue !== 'undefined') {
        setSelf(JSON.parse(savedValue));
    }
    onSet((newValue, _, isReset) => {
        isReset
            ? window.localStorage.removeItem(node.key)
            : window.localStorage.setItem(node.key, JSON.stringify(newValue));
    });
};

export const idbStorageEffect: AtomEffect<any> =({ setSelf, onSet, node, trigger }) => {
    if (trigger === 'get') {
        (async () => {
            const savedValue = await getItemPromise(node.key);
            // Only update if a saved value exists (not null or undefined)
            if (savedValue != null) {
                setSelf(savedValue);
            }
        })();
    }

    onSet(async (newValue, _, isReset) => {
        const db = await getDbPromise();
        if (isReset) {
            await db.delete(STORE, node.key);
        } else {
            await db.put(STORE, newValue, node.key);
        }
    });
};
